import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, mergeMap } from 'rxjs';

import { DocsActions } from '../docs/docs.actions';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { ArchitectureActions } from './architecture.actions';
import { compactChatMessages } from './architecture.models';

const ARCH_KEYS = [
  'architecture.main',
  'architecture.layout',
  'architecture.ink',
  'architecture.chat',
  'define.definition',
  'repos.settings',
] as const;

export const loadArchitecture$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.load),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([, pid]) => ARCH_KEYS.map((key) => DocsActions.loadDoc({ projectId: pid as string, key })))
    ),
  { functional: true }
);

export const saveDiagram$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.saveDiagram),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([{ diagram }, pid]) => {
        const data = { diagram };
        return [
          DocsActions.applyLocalData({ projectId: pid as string, key: 'architecture.main', data }),
          DocsActions.saveDoc({ projectId: pid as string, key: 'architecture.main', data }),
        ];
      })
    ),
  { functional: true }
);

const saveLayout$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.saveLayout),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([{ layout }, pid]) => {
        const data = layout ?? { positions: {} };
        return [
          DocsActions.applyLocalData({ projectId: pid as string, key: 'architecture.layout', data }),
          DocsActions.saveDoc({ projectId: pid as string, key: 'architecture.layout', data }),
        ];
      })
    ),
  { functional: true }
);

export const saveInk$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.saveInk),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([{ ink }, pid]) => {
        const data = ink ?? { strokes: [] };
        return [
          DocsActions.applyLocalData({ projectId: pid as string, key: 'architecture.ink', data }),
          DocsActions.saveDoc({ projectId: pid as string, key: 'architecture.ink', data }),
        ];
      })
    ),
  { functional: true }
);

/** Live ink draft: update the cache only (no persist). */
export const setInkDraft$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.setInkDraft),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([{ ink }, pid]) => [
        DocsActions.applyLocalData({ projectId: pid as string, key: 'architecture.ink', data: ink ?? { strokes: [] } }),
      ])
    ),
  { functional: true }
);

const setChatMessages$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ArchitectureActions.setChatMessages),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      filter(([, pid]) => !!pid),
      mergeMap(([{ messages }, pid]) => {
        const data = { messages: compactChatMessages(messages) };
        return [
          DocsActions.applyLocalData({ projectId: pid as string, key: 'architecture.chat', data }),
          DocsActions.saveDoc({ projectId: pid as string, key: 'architecture.chat', data }),
        ];
      })
    ),
  { functional: true }
);

export const architectureEffects = {
  loadArchitecture$,
  saveDiagram$,
  saveLayout$,
  saveInk$,
  setInkDraft$,
  setChatMessages$,
};
